/*
|--------------------------------------------------------------------------
| Resourceful Router Module
|--------------------------------------------------------------------------
|
| This module contains all the components needed for the resourceful router
| macro functionality, including validation, route generation, OpenAPI
| generation, and route registration services.
|
*/
import { negotiateContent } from './utils/http'
import { ResourcefulRouterVerb } from './types'
import { isAdditionalResourcefulRouteDetails } from './utils/type_guards'
import { ResourcefulHttpContext } from './services/resourceful_router_context_service'
import { ResourcefulModelController } from './controllers/resourceful_model_controller'
import { ResourcefulErrorMiddleware } from './middlewares/resourceful_error_middleware'
import { ResourcefulRouterOptionsService } from './services/resourceful_router_options_service'
import { ResourcefulRouterChoicesService } from './services/resourceful_router_choices_service'
import { ResourcefulRouterModelResolverService } from './services/resourceful_router_model_resolver_service'
import { ResourcefulRouterOpenApiDocumentService } from './services/resourceful_router_openapi_document_service'
import { ResourcefulRouterModelServiceOptionsService } from './services/resourceful_router_model_options_service'
import {
  E_UNRESOLVABLE_MODEL,
  E_INVALID_RESOURCEFUL_MODELS_MAP_AGGREGATE,
  E_INVALID_HTTP_METHOD_EXCEPTION,
} from '@nhtio/lucid-resourceful/errors'
import type { HttpContext, Router, RouteGroup } from '@adonisjs/core/http'
import type { ResourcefulHttpContextResolver } from './services/resourceful_router_context_service'
import type {
  RouteCallback,
  ResourcefulHttpVerb,
  MacroModelsMap,
  ResourcefulRouterOptions,
  ResourcefulRouterMacro,
  MiddlewareFn,
  ParsedNamedMiddleware,
} from './types'

class ResourcefulRouterService {
  readonly #router: Router
  readonly #rawModelMap: MacroModelsMap
  readonly #options?: Partial<ResourcefulRouterOptions>
  readonly #opts: ResourcefulRouterOptionsService
  readonly #modelMap: ResourcefulRouterModelResolverService
  readonly #controllers: Map<string, ResourcefulModelController>
  readonly #group: RouteGroup

  /** @private */
  constructor(
    router: Router,
    rawModelMap: MacroModelsMap,
    options?: Partial<ResourcefulRouterOptions>
  ) {
    this.#router = router
    this.#rawModelMap = rawModelMap
    this.#options = options
    this.#controllers = new Map<string, ResourcefulModelController>()
    this.#opts = ResourcefulRouterOptionsService.init(this.#options)
    this.#modelMap = ResourcefulRouterModelResolverService.init(this.#rawModelMap)
    const unresolvableModels: E_UNRESOLVABLE_MODEL[] = []
    Object.getOwnPropertyNames(this.#modelMap).forEach((name) => {
      const promiseOrError = this.#modelMap[name]
      if (promiseOrError instanceof E_UNRESOLVABLE_MODEL) {
        unresolvableModels.push(promiseOrError)
      } else if ('undefined' !== typeof promiseOrError) {
        const modelOptions = ResourcefulRouterModelServiceOptionsService.init(
          this.#rawModelMap[name as any]
        )
        const controller = new ResourcefulModelController(
          name,
          promiseOrError,
          modelOptions,
          this.#opts.headers,
          this.#opts.scopeRestrictors,
          this.#opts.payloadRestrictors,
          this.#opts.mutators,
          this.#opts.policies
        )
        this.#controllers.set(name, controller)
      }
    })
    if (unresolvableModels.length > 0) {
      throw new E_INVALID_RESOURCEFUL_MODELS_MAP_AGGREGATE(unresolvableModels)
    }
    this.#group = this.#router.group(this.#groupCallback.bind(this))
    if (this.#opts.prefix !== '/') {
      this.#group.prefix(this.#opts.prefix)
    }
    if (Array.isArray(this.#opts.middleware) && this.#opts.middleware.length > 0) {
      this.#group.use(Array.from(this.#opts.middleware))
    } else if (!Array.isArray(this.#opts.middleware) && this.#opts.middleware) {
      this.#group.use(this.#opts.middleware as any)
    }
    if ('string' === typeof this.#opts.domain && this.#opts.domain.length > 0) {
      this.#group.domain(this.#opts.domain)
    }
  }

  #groupCallback() {
    this.#controllers.forEach((controller, name) => {
      const controllerGroup = this.#router
        .group(() => {
          let crudActionMiddlewares = this.#modelMap.getCrudActionMiddlewaresForModel(name)
          if ('undefined' === typeof crudActionMiddlewares) {
            crudActionMiddlewares = Object.fromEntries(
              [...ResourcefulRouterVerb].map((verb) => [verb, []])
            )
          }
          ResourcefulRouterVerb.forEach((verb) => {
            crudActionMiddlewares![verb] = Array.isArray(crudActionMiddlewares![verb])
              ? crudActionMiddlewares![verb]
              : 'undefined' === typeof crudActionMiddlewares![verb]
                ? []
                : [crudActionMiddlewares![verb]]
          })
          const modelActionMiddlewares: {
            [K in ResourcefulRouterVerb]: Array<MiddlewareFn | ParsedNamedMiddleware>
          } = crudActionMiddlewares as any
          this.#router.any(
            `/$meta.choices/*`,
            this.#processRequest.bind(this, this.#serveResourcefulChoices.bind(this))
          )
          Object.entries(controller.options.additional).forEach(([methodAndPath, callback]) => {
            const [method, path] = methodAndPath.split('|') as [ResourcefulHttpVerb, string]
            if (!method || !path) return
            switch (method) {
              case 'get':
                if (isAdditionalResourcefulRouteDetails(callback)) {
                  this.#router.get(
                    path,
                    this.#processRequest.bind(
                      this,
                      this.#serveInResourcefulModelRouterContext.bind(
                        null,
                        controller,
                        callback.handler
                      )
                    )
                  )
                } else {
                  this.#router.get(
                    path,
                    this.#processRequest.bind(
                      this,
                      this.#serveInResourcefulModelRouterContext.bind(null, controller, callback)
                    )
                  )
                }
                break
              case 'post':
                if (isAdditionalResourcefulRouteDetails(callback)) {
                  this.#router.post(
                    path,
                    this.#processRequest.bind(
                      this,
                      this.#serveInResourcefulModelRouterContext.bind(
                        null,
                        controller,
                        callback.handler
                      )
                    )
                  )
                } else {
                  this.#router.post(
                    path,
                    this.#processRequest.bind(
                      this,
                      this.#serveInResourcefulModelRouterContext.bind(null, controller, callback)
                    )
                  )
                }
                break
              case 'put':
                if (isAdditionalResourcefulRouteDetails(callback)) {
                  this.#router.put(
                    path,
                    this.#processRequest.bind(
                      this,
                      this.#serveInResourcefulModelRouterContext.bind(
                        null,
                        controller,
                        callback.handler
                      )
                    )
                  )
                } else {
                  this.#router.put(
                    path,
                    this.#processRequest.bind(
                      this,
                      this.#serveInResourcefulModelRouterContext.bind(null, controller, callback)
                    )
                  )
                }
                break
              case 'patch':
                if (isAdditionalResourcefulRouteDetails(callback)) {
                  this.#router.patch(
                    path,
                    this.#processRequest.bind(
                      this,
                      this.#serveInResourcefulModelRouterContext.bind(
                        null,
                        controller,
                        callback.handler
                      )
                    )
                  )
                } else {
                  this.#router.patch(
                    path,
                    this.#processRequest.bind(
                      this,
                      this.#serveInResourcefulModelRouterContext.bind(null, controller, callback)
                    )
                  )
                }
                break
              case 'delete':
                if (isAdditionalResourcefulRouteDetails(callback)) {
                  this.#router.delete(
                    path,
                    this.#processRequest.bind(
                      this,
                      this.#serveInResourcefulModelRouterContext.bind(
                        null,
                        controller,
                        callback.handler
                      )
                    )
                  )
                } else {
                  this.#router.delete(
                    path,
                    this.#processRequest.bind(
                      this,
                      this.#serveInResourcefulModelRouterContext.bind(null, controller, callback)
                    )
                  )
                }
                break
              default:
                throw new E_INVALID_HTTP_METHOD_EXCEPTION([method, path])
            }
          })
          if (
            !this.#opts.except.includes('index') &&
            !controller.options.except.includes('index')
          ) {
            this.#router
              .get(
                '/',
                this.#processRequest.bind(this, controller.serveModelIndex.bind(controller))
              )
              .use(modelActionMiddlewares['index'] as any)
            this.#router
              .get(
                '/s/:encoded',
                this.#processRequest.bind(this, controller.serveModelShortcut.bind(controller))
              )
              .use(modelActionMiddlewares['index'] as any)
            this.#router.get(
              '/$meta.index',
              this.#processRequest.bind(this, controller.serveModelIndexMeta.bind(controller))
            )
          }
          if (
            !this.#opts.except.includes('create') &&
            !controller.options.except.includes('create')
          ) {
            this.#router
              .post(
                '/',
                this.#processRequest.bind(this, controller.serveModelCreate.bind(controller))
              )
              .use(modelActionMiddlewares['create'] as any)
            this.#router.get(
              '/$meta.create',
              this.#processRequest.bind(this, controller.serveModelCreateMeta.bind(controller))
            )
          }
          if (
            !this.#opts.except.includes('update') &&
            !controller.options.except.includes('update')
          ) {
            this.#router.get(
              '/$meta.update',
              this.#processRequest.bind(this, controller.serveModelUpdateMeta.bind(controller))
            )
          }
          if (
            !this.#opts.except.includes('bulkUpdate') &&
            !controller.options.except.includes('bulkUpdate')
          ) {
            this.#router.get(
              '/$meta.$bulk.update',
              this.#processRequest.bind(this, controller.serveModelBulkUpdateMeta.bind(controller))
            )
          }
          if (!this.#opts.except.includes('read') && !controller.options.except.includes('read')) {
            this.#router
              .get(
                '/:id',
                this.#processRequest.bind(this, controller.serveModelRead.bind(controller))
              )
              .use(modelActionMiddlewares['read'] as any)
          }
          if (
            !this.#opts.except.includes('readRelated') &&
            !controller.options.except.includes('readRelated')
          ) {
            this.#router
              .get(
                '/:id/:relationship',
                this.#processRequest.bind(this, controller.serveModelReadRelated.bind(controller))
              )
              .use(modelActionMiddlewares['readRelated'] as any)
            this.#router
              .get(
                '/:id/:relationship/s/:encoded',
                this.#processRequest.bind(
                  this,
                  controller.serveModelReadRelatedShortcut.bind(controller)
                )
              )
              .use(modelActionMiddlewares['readRelated'] as any)
            this.#router.get(
              '/:id/:relationship/$meta.index',
              this.#processRequest.bind(this, controller.serveModelReadRelatedMeta.bind(controller))
            )
          }
          if (
            !this.#opts.except.includes('bulkUpdate') &&
            !controller.options.except.includes('bulkUpdate')
          ) {
            this.#router
              .put('/$bulk', controller.serveModelBulkUpdate.bind(controller))
              .use(modelActionMiddlewares['bulkUpdate'] as any)
            this.#router
              .put('/$bulk/:ids', controller.serveModelBulkUpdate.bind(controller))
              .use(modelActionMiddlewares['bulkUpdate'] as any)
            this.#router
              .patch('/$bulk', controller.serveModelBulkUpdate.bind(controller))
              .use(modelActionMiddlewares['bulkUpdate'] as any)
            this.#router
              .patch('/$bulk/:ids', controller.serveModelBulkUpdate.bind(controller))
              .use(modelActionMiddlewares['bulkUpdate'] as any)
          }
          if (
            !this.#opts.except.includes('syncRelated') &&
            !controller.options.except.includes('syncRelated')
          ) {
            this.#router
              .put(
                '/:id/:relationship',
                this.#processRequest.bind(this, controller.serveModelSyncRelated.bind(controller))
              )
              .use(modelActionMiddlewares['syncRelated'] as any)
            this.#router
              .patch(
                '/:id/:relationship',
                this.#processRequest.bind(this, controller.serveModelSyncRelated.bind(controller))
              )
              .use(modelActionMiddlewares['syncRelated'] as any)
          }
          if (
            !this.#opts.except.includes('update') &&
            !controller.options.except.includes('update')
          ) {
            this.#router
              .put(
                '/',
                this.#processRequest.bind(this, controller.serveModelUpdate.bind(controller))
              )
              .use(modelActionMiddlewares['update'] as any)
            this.#router
              .put(
                '/:id',
                this.#processRequest.bind(this, controller.serveModelUpdate.bind(controller))
              )
              .use(modelActionMiddlewares['update'] as any)
            this.#router
              .patch(
                '/:id',
                this.#processRequest.bind(this, controller.serveModelUpdate.bind(controller))
              )
              .use(modelActionMiddlewares['update'] as any)
          }
          if (
            !this.#opts.except.includes('delete') &&
            !controller.options.except.includes('delete')
          ) {
            this.#router
              .delete(
                '/:id',
                this.#processRequest.bind(this, controller.serveModelDelete.bind(controller))
              )
              .use(modelActionMiddlewares['delete'] as any)
          }
        })
        .prefix(name)
      if (controller.options.middleware) {
        if (
          Array.isArray(controller.options.middleware) &&
          controller.options.middleware.length > 0
        ) {
          controllerGroup.use(Array.from(controller.options.middleware))
        } else if (!Array.isArray(controller.options.middleware) && controller.options.middleware) {
          controllerGroup.use(controller.options.middleware as any)
        }
      }
    })
    Object.entries(this.#opts.additional).forEach(([methodAndPath, callback]) => {
      const [method, path] = methodAndPath.split('|') as [ResourcefulHttpVerb, string]
      if (!method || !path) return
      if (isAdditionalResourcefulRouteDetails(callback)) {
        this.#router[method](path, this.#processRequest.bind(this, callback.handler))
      } else {
        this.#router[method](path, this.#processRequest.bind(this, callback))
      }
    })
    this.#router.any(
      '/$meta.choices/*',
      this.#processRequest.bind(this, this.#serveResourcefulChoices.bind(this))
    )
    this.#router.get('/', this.#serveResourcefulRouteIndex.bind(this))
  }

  async #processRequest(callback: RouteCallback, ctx: HttpContext) {
    let next: ((ctx: HttpContext) => unknown | PromiseLike<unknown> | Promise<unknown>) | undefined
    if (typeof callback === 'function') {
      next = async (c) => await callback(c)
    } else if (Array.isArray(callback) && callback.length === 2) {
      const [maybeResolvableController, method] = callback
      if (
        method in maybeResolvableController &&
        typeof maybeResolvableController[method] === 'function'
      ) {
        // @ts-ignore
        next = async (c) => await maybeResolvableController[method](c)
      } else {
        // @ts-ignore
        const resolvedController = maybeResolvableController()
        if (typeof resolvedController[method] === 'function') {
          next = async (c) => await resolvedController[method](c)
        }
      }
    }
    if (!next) return
    try {
      await next(ctx)
    } catch (error) {
      if (this.#opts.catchThrown && ResourcefulErrorMiddleware.shouldHandle(ctx)) {
        return await ResourcefulErrorMiddleware.handle(error, ctx, {
          onVineValidationError: this.#opts.onVineValidationError,
          onJoiValidationError: this.#opts.onJoiValidationError,
          onException: this.#opts.onException,
          onError: this.#opts.onError,
          onUnknown: this.#opts.onUnknown,
          onAny: this.#opts.onAny,
          asYaml: true,
          headers: this.#opts.headers,
        })
      } else {
        throw error
      }
    }
  }

  async #serveInResourcefulModelRouterContext(
    controller: ResourcefulModelController,
    callback: RouteCallback,
    ctx: HttpContext
  ) {
    const resolver: ResourcefulHttpContextResolver = controller.resolveRequestRequirements.bind(
      controller,
      ctx
    ) as ResourcefulHttpContextResolver
    await ResourcefulHttpContext.run({ resolver }, async () => {
      if (typeof callback === 'function') {
        await callback(ctx)
      } else if (Array.isArray(callback) && callback.length === 2) {
        const [maybeResolvableController, method] = callback
        if (
          method in maybeResolvableController &&
          typeof maybeResolvableController[method] === 'function'
        ) {
          // @ts-ignore
          await maybeResolvableController[method](ctx)
        } else {
          // @ts-ignore
          const resolvedController = maybeResolvableController()
          if (typeof resolvedController[method] === 'function') {
            await resolvedController[method](ctx)
          }
        }
      }
    })
  }

  async #serveResourcefulChoices(ctx: HttpContext) {
    return await ResourcefulRouterChoicesService.serve(
      ctx,
      this.#opts,
      this.#modelMap,
      this.#controllers
    )
  }

  async #serveResourcefulRouteIndex(ctx: HttpContext) {
    const serveAs = negotiateContent(ctx)
    if (serveAs === 'html') {
      return await this.#serveResourcefulRouteIndexHtml(ctx)
    }
    const { default: app } = await import('@adonisjs/core/services/app')
    const service = new ResourcefulRouterOpenApiDocumentService(
      this.#opts,
      this.#modelMap,
      this.#controllers,
      app
    )
    await service.boot(ctx)
    return await service.serveAs(serveAs, ctx)
  }

  async #serveResourcefulRouteIndexHtml(ctx: HttpContext) {
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title inertia>${this.#opts.info.title}</title><script src="https://unpkg.com/@stoplight/elements/web-components.min.js"></script><link rel="stylesheet" href="https://unpkg.com/@stoplight/elements/styles.min.css"><style>elements-api{min-height:100dvh}elements-api .sl-h-full{min-height:100dvh}</style></head><body><elements-api apiDescriptionUrl=${JSON.stringify(ctx.request.url())} router="hash" layout="responsive" /></body></html>`
    ctx.response.type('text/html')
    ctx.response.status(200)
    ctx.response.send(html)
  }

  get group() {
    return this.#group
  }

  public static hook(
    router: Router,
    rawModelMap: MacroModelsMap,
    options?: Partial<ResourcefulRouterOptions>
  ): RouteGroup {
    const instance = new ResourcefulRouterService(router, rawModelMap, options)
    return instance.group
  }
}

export const resourcefulRouterMacro: ResourcefulRouterMacro = function (
  this: Router,
  models: MacroModelsMap,
  options: Partial<ResourcefulRouterOptions> = {}
): RouteGroup {
  return ResourcefulRouterService.hook(this, models, options)
}

export type { ResourcefulRouterService }

export type GetResourcefulModelCreatePayloadValidationSchemaArgs = Parameters<
  (typeof ResourcefulModelController)['getResourcefulModelCreatePayloadValidationSchema']
>

export const getResourcefulModelCreatePayloadValidationSchema = (
  ...args: GetResourcefulModelCreatePayloadValidationSchemaArgs
) => ResourcefulModelController.getResourcefulModelCreatePayloadValidationSchema(...args)
